/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState } from 'react';
import { Database, RefreshCw, Wifi, Check, X } from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';
import type { GCSIntegrationOrgSettings } from '@shared/types/organizationSettings';

interface GCSConnectionStatus {
  tested: boolean;
  success: boolean;
  error?: string;
  details?: {
    bucketName?: string;
    location?: string;
    filesFound?: number;
  };
}

interface GCSIntegrationSectionProps {
  settings: GCSIntegrationOrgSettings;
  fallbackProjectId: string;
  onChange: (settings: GCSIntegrationOrgSettings) => void;
  onDirty: () => void;
}

export function GCSIntegrationSection({ 
  settings, 
  fallbackProjectId,
  onChange, 
  onDirty 
}: GCSIntegrationSectionProps) {
  const { toast } = useToast();
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<GCSConnectionStatus>({ 
    tested: false, 
    success: false 
  });

  const testConnection = async () => {
    const effectiveProjectId = settings.projectId || fallbackProjectId;
    
    if (!effectiveProjectId) {
      setConnectionStatus({
        tested: true,
        success: false,
        error: 'Please provide a GCP Project ID (either in GCS settings or shared GCP Project settings)'
      });
      return;
    }
    
    if (!settings.bucketName) {
      setConnectionStatus({
        tested: true,
        success: false,
        error: 'Please provide a GCS bucket name'
      });
      return;
    }
    
    setIsTestingConnection(true);
    setConnectionStatus({ tested: false, success: false });
    
    try {
      const response = await fetch('/api/settings/gcs/test-connection', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: effectiveProjectId,
          bucketName: settings.bucketName,
          bucketPrefix: settings.bucketPrefix || ''
        })
      });
      
      const result = await response.json();
      
      if (result.success && result.data?.success) {
        setConnectionStatus({ 
          tested: true, 
          success: true,
          details: result.data.details
        });
        toast({
          title: 'Connection successful',
          description: result.data.message || 'Successfully connected to GCS bucket',
        });
      } else {
        setConnectionStatus({
          tested: true,
          success: false,
          error: result.data?.message || result.error?.message || 'Connection failed'
        });
      }
    } catch (error: any) {
      setConnectionStatus({
        tested: true,
        success: false,
        error: error.message || 'Connection test failed'
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  const effectiveProjectId = settings.projectId || fallbackProjectId;

  return (
    <div style={{ 
      marginBottom: '32px',
      padding: '20px',
      background: 'linear-gradient(135deg, #fef9f0 0%, #fef3c7 100%)',
      borderRadius: '8px',
      border: '1px solid #fcd34d'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Database size={20} style={{ marginRight: '8px', color: '#d97706' }} />
        <h3 style={{ margin: 0, color: '#78350f' }}>Google Cloud Storage (GCS) for Playbooks</h3>
      </div>
      
      <p style={{ fontSize: '13px', color: '#92400e', marginBottom: '16px' }}>
        Configure GCS as a data source for Playbooks and Knowledge Mining. When enabled, you can mine knowledge 
        from documents stored in GCS buckets to generate playbooks automatically.
      </p>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={settings.enabled}
            onChange={(e) => {
              onChange({ ...settings, enabled: e.target.checked });
              onDirty();
            }}
          />
          <span style={{ fontSize: '14px', color: '#78350f' }}>Enable GCS integration for Playbook data sources</span>
        </label>
      </div>
      
      {settings.enabled && (
        <div style={{ 
          padding: '16px', 
          background: '#fff',
          borderRadius: '6px',
          border: '1px solid #fde68a'
        }}>
          <div className="form-group">
            <label htmlFor="gcs-project-id">GCP Project ID <span style={{ color: '#dc2626' }}>*</span></label>
            <input
              id="gcs-project-id"
              type="text"
              placeholder="Enter your GCP project ID"
              value={settings.projectId}
              onChange={(e) => {
                onChange({ ...settings, projectId: e.target.value });
                onDirty();
              }}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
            <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
              The GCP project containing your GCS bucket (pre-populated from shared GCP settings if configured)
            </small>
          </div>
          
          <div className="form-group">
            <label htmlFor="gcs-bucket-name">GCS Bucket Name <span style={{ color: '#dc2626' }}>*</span></label>
            <input
              id="gcs-bucket-name"
              type="text"
              placeholder="Enter your bucket name (required)"
              value={settings.bucketName}
              onChange={(e) => {
                onChange({ ...settings, bucketName: e.target.value });
                onDirty();
                setConnectionStatus({ tested: false, success: false });
              }}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
            <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
              The GCS bucket containing documents for Knowledge Mining (required for testing connection)
            </small>
          </div>
          
          <div className="form-group">
            <label htmlFor="gcs-bucket-prefix">Bucket Prefix (Optional)</label>
            <input
              id="gcs-bucket-prefix"
              type="text"
              placeholder="docs/playbooks/"
              value={settings.bucketPrefix}
              onChange={(e) => {
                onChange({ ...settings, bucketPrefix: e.target.value });
                onDirty();
                setConnectionStatus({ tested: false, success: false });
              }}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
            <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
              Optional folder path within the bucket (e.g., "docs/playbooks/")
            </small>
          </div>
          
          <div style={{ 
            marginTop: '12px',
            padding: '12px',
            background: '#fef3c7',
            borderRadius: '4px',
            border: '1px solid #fcd34d',
            fontSize: '13px',
            color: '#92400e'
          }}>
            <strong>Permissions:</strong> Your GCP account needs the following permissions on the bucket:
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
              <li>Storage Object Viewer (to read documents)</li>
              <li>Storage Object Creator (to write generated playbooks, optional)</li>
            </ul>
          </div>
          
          {/* Test Connection Button */}
          <div style={{ marginTop: '16px' }}>
            <button
              className="btn"
              onClick={testConnection}
              disabled={isTestingConnection || !effectiveProjectId || !settings.bucketName}
              style={{
                width: 'auto',
                padding: '8px 16px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {isTestingConnection ? (
                <>
                  <RefreshCw size={16} className="spin" />
                  Testing Connection...
                </>
              ) : (
                <>
                  <Wifi size={16} />
                  Test Connection
                </>
              )}
            </button>
            
            {connectionStatus.tested && (
              <div style={{ marginTop: '12px' }}>
                {connectionStatus.success ? (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      <Check size={16} style={{ color: '#28a745', marginRight: '8px' }} />
                      <span style={{
                        padding: '4px 12px',
                        background: '#d1fae5',
                        color: '#065f46',
                        borderRadius: '4px',
                        fontSize: '13px',
                        fontWeight: 500
                      }}>
                        Connected to GCS bucket
                      </span>
                    </div>
                    {connectionStatus.details && (
                      <div style={{
                        padding: '8px 12px',
                        background: '#f0fdf4',
                        borderRadius: '4px',
                        fontSize: '12px',
                        color: '#166534'
                      }}>
                        <div><strong>Bucket:</strong> {connectionStatus.details.bucketName}</div>
                        <div><strong>Location:</strong> {connectionStatus.details.location}</div>
                        <div><strong>Files found:</strong> {connectionStatus.details.filesFound}</div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <X size={16} style={{ color: '#dc3545', marginRight: '8px' }} />
                    <span style={{
                      padding: '4px 12px',
                      background: '#fef2f2',
                      color: '#b91c1c',
                      borderRadius: '4px',
                      fontSize: '13px',
                      fontWeight: 500
                    }}>
                      {connectionStatus.error || 'Connection failed'}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
